// ─────────────────────────────────────────────────────────────────────────────
// C2 — «Participación por Campaña», el panel `rep-panel-campgeneral` de la maqueta.
//
// 🔴 Este tab pintaba OTRA tabla. Sus seis columnas (Nombre/Tipo/Estado/Audiencia/Reconocimientos/
// Expiración) son las de «Históricos por Campaña», que en la maqueta vive en el tab de al lado. Al
// medirlo panel a panel salió que los dos tabs de campañas tenían el contenido cruzado: esta tabla
// —Campaña · Reconocimientos · Participantes · Tasa · Prom/usuario · Estado · Acciones— NO EXISTÍA.
//
// Las dos columnas calculadas salen de la hoja «Definiciones» del Excel del cliente, literal:
//   · Tasa de participación      = Participantes ÷ Audiencia Total
//   · Promedio por participante  = Reconocimientos ÷ Participantes
//
// Y «Participantes» son personas ÚNICAS que ENVIARON al menos un reconocimiento — emisores. La cita
// exacta de `03 - Historicos.xlsx`: «Personas distintas que participaron enviando al menos un
// reconocimiento […] Conteo distinto de usuarios emisores». Este comentario afirmaba lo contrario
// («reconociendo o siendo reconocidas») atribuyéndoselo al cliente, y esa frase no aparece en
// ninguna hoja «Definiciones» de `Diseño/Exceles` — se corrigió en la ronda de UAT del 17-ago-2026,
// junto con el backend que la calculaba así. Ver `decisiones.md`.
//
// No aplica los filtros superiores: la lista es acotada y trae su propio buscador, orden y paginación.
//
// Fase D (D2) — el criterio de cierre de la fase agrega Tipo/Audiencia/Fecha de expiración también
// aquí (no solo en «Históricos»): esta tabla pasa a cubrir ambas lecturas —participación Y comparativa
// básica— sin duplicar la comparativa completa de nueve columnas del tab vecino.
//
// 🔴 CORREGIDO (24-ago-2026). Aquí decía «el estado ya nunca dice "Expirado": el dominio no tiene ese
// valor, solo Borrador/Activa/Finalizada», y con ese criterio la tabla mostraba «Finalizada». Eso
// contradice la Sección 7, que dice justo lo contrario y de forma explícita:
//
//   «Terminología a unificar: la UI usa "Expirado" para campañas finalizadas; el modelo de datos usa
//    el enum Finalizada (ver Sección 06, Diccionario de Datos).»
//
// El argumento anterior confundía las dos capas: que el ENUM no tenga «Expirado» es precisamente el
// motivo de esa regla, no una razón para no aplicarla. Y dejaba los dos tabs de Reportería en
// desacuerdo entre sí — «Históricos» ya rotulaba «Expirada» con su propio bloque de i18n.
// Se cambia el VALOR de `reports.campaigns.status.finalizada` en los 7 idiomas; el modelo no se toca.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// 2026-09-29 — ESTA PANTALLA ES AHORA LA PESTAÑA «HISTÓRICOS». Conserva el id interno `campaigns` (y
// este nombre de archivo) para no mover rutas ni pruebas. Cambio acotado por el cliente al documento de
// Adriana (`cambios reportes_Adry 2.docx`):
//   · lista SOLO las campañas cerradas (`campanaCerrada`: Finalizada, o fecha de fin ya vencida);
//   · sin las columnas «Estado» ni «Acciones» (el «Descargar Excel» por fila);
//   · trae el «Descargar reporte global» que vivía en el «Históricos» anterior, que se eliminó
//     (inventario y rollback: `docs/contexto/historicos-eliminado-rollback.md`).
// Los comentarios de arriba describen la versión previa de esta pantalla y se dejan como historia.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCampaigns, useCampaignSummaries, getCampaignName, getFundamentalName, getFundamentalColor } from '@/api/recognition';
import type { CampaignDto, CampaignSummaryDto, CampaignType } from '@/api/recognition';
import { useFundamentalsForReporting } from '@/hooks/useFundamentalsForReporting';
import { ReportStatus, SuppressedNote, AlcanceCell } from '../reportsShared';
import { campanaCerrada } from '../campanasReporte';
import { CampaignsGlobalExportButton } from './CampaignsGlobalExportButton';

const PAGE_SIZE = 10;

type SortKey = 'nombre' | 'reconocimientos' | 'participantes' | 'tasa' | 'promedio';
type SortDir = 'asc' | 'desc';
type Categoria = 'all' | CampaignType;

interface Row {
  campaign: CampaignDto;
  nombre: string;
  summary: CampaignSummaryDto | undefined;
  loadingSummary: boolean;
}

/**
 * Las dos columnas calculadas de la maqueta, con la definición del cliente.
 *
 * 🔴 Devuelven `null` —no 0— cuando el denominador falta o es cero. Un 0 % se leería como «no
 * participó nadie» y un 0,0 como «cada quien reconoció cero veces», cuando lo que pasa es que no se
 * puede calcular: la campaña no tiene audiencia registrada, o sus recibos son anteriores a la
 * proyección. Es el mismo criterio que `NoAplicaConFiltro` del tablero.
 *
 * 🔴 Devuelve el porcentaje SIN redondear a entero (24-ago→01-sep-2026, reporte real de Adriana:
 * "la tasa se está mostrando en ceros"). Con una Audiencia de miles y una decena de Participantes
 * — el caso normal de una campaña, no la excepción — el cociente real es, por ejemplo, 0,24 %:
 * `Math.round` a entero lo aplasta a `0 %`, indistinguible de "no participó nadie". El redondeo a 1
 * decimal (y el aviso `<0.1%` para lo que ni así se distingue de cero) vive en `formatTasaDisplay`,
 * que es quien decide cómo se VE — este helper decide cuánto VALE, y de eso depende también
 * `tasaTone` (el semáforo perdería precisión si comparara contra un entero ya aplastado).
 */
export function tasaDeParticipacion(participantes: number | undefined, audiencia: number | undefined): number | null {
  // 🔴 `participantes === 0` también sale «—», y no es un descuido. El backend emite 0 en DOS casos
  // que no puede distinguir: nadie participó, o los recibos no cubren esa campaña (es anterior a la
  // proyección, y el backfill está pendiente). Pintar «0 %» elegiría la primera lectura y la
  // presentaría como un hecho. El hueco no elige.
  if (!participantes || !audiencia) return null;
  return (participantes / audiencia) * 100;
}

export function promedioPorUsuario(reconocimientos: number | undefined, participantes: number | undefined): number | null {
  if (reconocimientos == null || !participantes) return null;
  return Math.round((reconocimientos / participantes) * 10) / 10;
}

/**
 * Semáforo de la Tasa de participación (`.rr-tasa--*`, RecognitionReports.css). Umbrales acordados
 * con el cliente: ≥70% alta, 40-69% media, <40% baja. `null` sin dato calculable — no se pinta.
 */
export function tasaTone(tasa: number | null): 'alta' | 'media' | 'baja' | null {
  if (tasa == null) return null;
  if (tasa >= 70) return 'alta';
  if (tasa >= 40) return 'media';
  return 'baja';
}

/**
 * Celda numérica del resumen, con sus tres estados bien separados: suprimido por umbral de
 * anonimato, todavía cargando, o sin dato. Estaba escrito como un ternario anidado en el JSX y los
 * tres casos se confundían — «—» significaba a la vez "suprimido" y "no hay dato".
 */
function summaryCell(value: number | undefined, suprimido: boolean, loading: boolean): string | number {
  if (suprimido) return '—';
  if (value !== undefined) return value;
  return loading ? '…' : '—';
}

interface CardSummary {
  participantesDisplay: string | number;
  tasaDisplay: string;
  hasSuprimido: boolean;
}

/**
 * Igual criterio que `summaryCell`, pero para el % ya formateado: evita el ternario anidado.
 *
 * Redondea a 1 decimal (25 % sigue viéndose "25%": un entero en JS no imprime ".0") y, por debajo de
 * 0,1 %, imprime `<0.1%` en vez de "0.0%" — mismo criterio ya usado en el embudo de Adopción
 * (`REPORTERIA-REGLAS.md`, sección 5) para no confundir "casi cero" con "cero".
 */
export function formatTasaDisplay(tasaValue: number | null, isLoading: boolean): string {
  if (tasaValue == null) return isLoading ? '…' : '—';
  if (tasaValue > 0 && tasaValue < 0.1) return '<0.1%';
  return `${Math.round(tasaValue * 10) / 10}%`;
}

/**
 * D4 — las dos tarjetas resumen de «Participación por Campaña», con el mismo comportamiento
 * contextual del selector de categoría/buscador: con exactamente UNA campaña visible (el buscador
 * la aisló, o la categoría elegida solo tiene esa), se muestran sus propios números; con cero o
 * varias, el agregado de las que están a la vista.
 *
 * El agregado SOLO suma filas no suprimidas: una fila enmascarada por el umbral de anonimato emite
 * 0 desde el backend (ver `CampaignSummaryDto.participantes`), y sumarlo tal cual inflaría un
 * "agregado" con un cero que no es un dato real. `hasSuprimido` avisa que el total puede quedar
 * corto por eso — la nota reutiliza `SuppressedNote`, ya usada en el resto de Reportería.
 */
function buildCardSummary(items: Row[]): CardSummary {
  const hasSuprimido = items.some((row) => row.summary?.suprimido);

  if (items.length === 1) {
    const [only] = items;
    const summary = only.summary;
    const suprimido = summary?.suprimido ?? false;
    const participantesDisplay = summaryCell(summary?.participantes, suprimido, only.loadingSummary);
    if (suprimido) {
      return { participantesDisplay, tasaDisplay: '—', hasSuprimido };
    }
    const tasaValue = tasaDeParticipacion(summary?.participantes, summary?.audiencia);
    const tasaDisplay = formatTasaDisplay(tasaValue, only.loadingSummary);
    return { participantesDisplay, tasaDisplay, hasSuprimido };
  }

  const isLoading = items.some((row) => row.loadingSummary);
  if (isLoading) {
    return { participantesDisplay: '…', tasaDisplay: '…', hasSuprimido };
  }

  const visibles = items.filter((row) => row.summary && !row.summary.suprimido);
  if (visibles.length === 0) {
    return { participantesDisplay: '—', tasaDisplay: '—', hasSuprimido };
  }

  const totalParticipantes = visibles.reduce((sum, row) => sum + (row.summary?.participantes ?? 0), 0);
  const totalAudiencia = visibles.reduce((sum, row) => sum + (row.summary?.audiencia ?? 0), 0);
  const tasaValue = tasaDeParticipacion(totalParticipantes, totalAudiencia);

  return {
    participantesDisplay: totalParticipantes,
    tasaDisplay: formatTasaDisplay(tasaValue, false),
    hasSuprimido,
  };
}

// RF-151 — el historial cubre TODAS las campañas y trae su propio buscador, orden y paginación:
// "sin filtros superiores". No recibe `params` (antes lo aceptaba y lo descartaba con `void`) y la
// barra se oculta mientras está activo (D-13).
/** `canExportData` default `true`: ver nota en `DailyTab.tsx`. */
export function CampaignsGeneralTab({ canExportData = true }: Readonly<{ canExportData?: boolean }> = {}) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState<Categoria>('all');
  const [sortKey, setSortKey] = useState<SortKey>('reconocimientos');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);

  // Mismo patrón que `DistribucionFundamental.tsx`: nombre/color resueltos contra el catálogo real
  // (nunca inventados en el cliente), con `f as never` porque el catálogo de reportería y
  // `FundamentalDto` comparten los campos que ambos helpers leen.
  const fundamentalsCatalog = useFundamentalsForReporting();
  const fundamentalById = useMemo(
    () => new Map((fundamentalsCatalog.data ?? []).map((f, i) => [
      f.id,
      { nombre: getFundamentalName(f as never, lang), color: getFundamentalColor(f as never, i) },
    ])),
    [fundamentalsCatalog.data, lang],
  );

  const campaigns = useCampaigns(undefined, 1, 100);
  // La lista COMPLETA —borradores y activas incluidas— es lo que recibe el botón del reporte global:
  // el backend agrega TODAS las campañas del alcance, y el Resumen Ejecutivo solo cuadra con las filas
  // si las dos partes hablan del mismo conjunto. La TABLA, en cambio, muestra solo las cerradas.
  const todasLasCampanas = useMemo(() => campaigns.data?.items ?? [], [campaigns.data]);
  const cerradas = useMemo(() => todasLasCampanas.filter((c) => campanaCerrada(c)), [todasLasCampanas]);
  const campaignIds = useMemo(() => cerradas.map((c) => c.id), [cerradas]);
  const summaries = useCampaignSummaries(campaignIds);

  const rows = useMemo<Row[]>(
    () => cerradas.map((campaign, i) => ({
      campaign,
      nombre: getCampaignName(campaign, lang),
      summary: summaries[i]?.data,
      loadingSummary: summaries[i]?.isLoading ?? false,
    })),
    [cerradas, summaries, lang],
  );

  const filtered = useMemo(
    () => rows.filter((r) => r.nombre.toLowerCase().includes(search.toLowerCase())
      && (categoria === 'all' || r.campaign.tipo === categoria)),
    [rows, search, categoria],
  );

  const cardSummary = useMemo(() => buildCardSummary(filtered), [filtered]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'nombre': return a.nombre.localeCompare(b.nombre) * dir;
        case 'reconocimientos':
          return ((a.summary?.reconocimientos ?? -1) - (b.summary?.reconocimientos ?? -1)) * dir;
        case 'participantes':
          return ((a.summary?.participantes ?? -1) - (b.summary?.participantes ?? -1)) * dir;
        // Las derivadas se ordenan por su VALOR CALCULADO, no por el numerador: ordenar la tasa por
        // participantes pondría arriba la campaña más grande, no la que mejor participa.
        case 'tasa':
          return ((tasaDeParticipacion(a.summary?.participantes, a.summary?.audiencia) ?? -1)
                - (tasaDeParticipacion(b.summary?.participantes, b.summary?.audiencia) ?? -1)) * dir;
        case 'promedio':
          return ((promedioPorUsuario(a.summary?.reconocimientos, a.summary?.participantes) ?? -1)
                - (promedioPorUsuario(b.summary?.reconocimientos, b.summary?.participantes) ?? -1)) * dir;
        default: return 0;
      }
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  const isEmpty = !campaigns.isLoading && rows.length === 0;
  const cardParticipantes = campaigns.isLoading ? '…' : cardSummary.participantesDisplay;
  const cardTasa = campaigns.isLoading ? '…' : cardSummary.tasaDisplay;

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-list" aria-hidden="true" /> {t('reports.campaigns.title')}
          </div>
          <div className="au-card-sub">{t('reports.campaigns.subtitle')}</div>
        </div>
        {/* Reporte de TODAS las campañas en un solo Excel (antes en el «Históricos» anterior). Va en la
            cabecera porque aplica al listado completo, no a una fila; la descarga por fila («Acciones»)
            se retiró. Recibe la lista completa, no la filtrada de la tabla. */}
        <CampaignsGlobalExportButton campaigns={todasLasCampanas} canExportData={canExportData} />
      </div>

      <div className="rr-actions">
        <input
          type="text"
          className="au-date-input"
          placeholder={t('reports.campaigns.search')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          aria-label={t('reports.campaigns.search')}
        />
        <select
          className="au-cascade-select"
          value={categoria}
          onChange={(e) => { setCategoria(e.target.value as Categoria); setPage(1); }}
          aria-label={t('reports.campaigns.filterTypeLabel')}
        >
          <option value="all">{t('reports.campaigns.filterAllTypes')}</option>
          <option value="Fundamentales">{t('reports.campaigns.type.Fundamentales')}</option>
          <option value="Brillamos">{t('reports.campaigns.type.Brillamos')}</option>
          <option value="Personalizada">{t('reports.campaigns.type.Personalizada')}</option>
        </select>
      </div>

      {/* D4 — Total de participantes / % de participación: de la campaña única que el buscador o la
          categoría aislaron, o el agregado de las que quedan a la vista si no hay una sola. */}
      {!campaigns.isLoading && cardSummary.hasSuprimido && <SuppressedNote />}
      <div className="rr-summary-cards">
        <div className="rr-summary-card">
          <div className="rr-summary-label">{t('reports.campaigns.summaryParticipants')}</div>
          <div className="rr-summary-value" data-testid="campaign-summary-participants-value">
            {cardParticipantes}
          </div>
        </div>
        <div className="rr-summary-card">
          <div className="rr-summary-label">{t('reports.campaigns.summaryRate')}</div>
          <div className="rr-summary-value rr-summary-value--rate" data-testid="campaign-summary-rate-value">
            {cardTasa}
          </div>
        </div>
      </div>

      <ReportStatus
        isLoading={campaigns.isLoading}
        isError={campaigns.isError}
        error={campaigns.error}
        isEmpty={isEmpty}
        onRetry={() => campaigns.refetch()}
      >
        {sorted.length === 0 ? (
          <div className="rr-state">{t('reports.common.empty')}</div>
        ) : (
          <>
            <div className="rr-table-wrap">
              <table className="rr-table">
                <thead>
                  <tr>
                    <th><button type="button" className="rr-sort-btn" onClick={() => toggleSort('nombre')}>{t('reports.campaigns.colName')}</button></th>
                    <th>{t('reports.campaigns.colType')}</th>
                    <th className="rr-num"><button type="button" className="rr-sort-btn" onClick={() => toggleSort('reconocimientos')}>{t('reports.campaigns.colRecognitions')}</button></th>
                    <th className="rr-num"><button type="button" className="rr-sort-btn" onClick={() => toggleSort('participantes')}>{t('reports.campaigns.colParticipants')}</button></th>
                    {/* UAT 17-ago-2026 — «Audiencia» conserva su significado (número de empleados,
                        denominador de la tasa) y ahora dice su unidad en el título; «Alcance» es la
                        columna nueva que responde a qué OUs se dirigió la campaña. */}
                    <th className="rr-num" title={t('reports.common.colAudienceHelp')}>{t('reports.campaigns.colAudience')}</th>
                    <th title={t('reports.common.colScopeHelp')}>{t('reports.common.colScope')}</th>
                    <th className="rr-num"><button type="button" className="rr-sort-btn" onClick={() => toggleSort('tasa')}>{t('reports.campaigns.colRate')}</button></th>
                    {/* UAT 17-ago-2026 — «Prom./usuario» no se entendía ("no tengo idea de qué se
                        refiera", Adriana). La métrica es del propio cliente: la hoja «Definiciones»
                        de `03 - Historicos.xlsx` la llama «Promedio de reconocimientos por persona
                        de los participantes» y la calcula como Reconocimientos ÷ Participantes. Lo
                        que fallaba era la abreviatura, no el dato: se usa el nombre del cliente y su
                        definición literal como tooltip, que hasta ahora solo existía en la hoja
                        «Definiciones» del Excel exportado — es decir, había que descargar el archivo
                        para saber qué decía la columna que ya estaba en pantalla. */}
                    <th className="rr-num" title={t('reports.campaigns.colAvgUserHelp')}>
                      <button type="button" className="rr-sort-btn" onClick={() => toggleSort('promedio')}>{t('reports.campaigns.colAvgUser')}</button>
                    </th>
                    {/* BJ-H2/BJ-H3 (REPORTERIA-REGLAS.md §8.3) — «Fecha de inicio» nueva; «Fecha de fin»
                        homologa la antigua «Expiración». */}
                    <th>{t('reports.campaigns.colStart')}</th>
                    <th>{t('reports.campaigns.colExpiration')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.campaign.id}>
                      <td>{r.nombre}</td>
                      <td>{t(`reports.campaigns.type.${r.campaign.tipo}`)}</td>
                      <td className="rr-num">
                        {(() => {
                          const valor = summaryCell(r.summary?.reconocimientos, r.summary?.suprimido ?? false, r.loadingSummary);
                          const predominante = r.summary?.suprimido
                            ? undefined
                            : fundamentalById.get(r.summary?.fundamentalPredominanteId ?? '');
                          // El tooltip es lo que explica el color: sin él, un número naranja o morado
                          // no dice nada por sí mismo — hay que poder pasar el cursor y leer POR QUÉ.
                          return predominante
                            ? (
                              <span
                                style={{ color: predominante.color, fontWeight: 700 }}
                                title={t('reports.campaigns.dominantFundamentalTooltip', { name: predominante.nombre })}
                              >
                                {valor}
                              </span>
                            )
                            : valor;
                        })()}
                      </td>
                      <td className="rr-num">
                        {summaryCell(r.summary?.participantes, r.summary?.suprimido ?? false, r.loadingSummary)}
                      </td>
                      <td className="rr-num">
                        {summaryCell(r.summary?.audiencia, r.summary?.suprimido ?? false, r.loadingSummary)}
                      </td>
                      {/* El alcance sale de la CAMPAÑA, no del resumen: no depende del backend de
                          analítica ni se suprime por anonimato — no es una métrica de personas, es
                          la configuración de a quién se dirigió. */}
                      <td><AlcanceCell alcance={r.campaign.alcance ?? []} /></td>
                      {/* Las derivadas heredan la supresión: si el conteo está tapado por anonimato,
                          una tasa calculada sobre él lo destaparía por división. */}
                      <td className="rr-num">
                        {(() => {
                          if (r.summary?.suprimido) return '—';
                          const v = tasaDeParticipacion(r.summary?.participantes, r.summary?.audiencia);
                          if (v == null) return r.loadingSummary ? '…' : '—';
                          const tono = tasaTone(v);
                          return <span className={tono ? `rr-tasa--${tono}` : undefined}>{formatTasaDisplay(v, false)}</span>;
                        })()}
                      </td>
                      <td className="rr-num">
                        {r.summary?.suprimido
                          ? '—'
                          : (() => {
                              const v = promedioPorUsuario(r.summary?.reconocimientos, r.summary?.participantes);
                              return v == null ? (r.loadingSummary ? '…' : '—') : v.toFixed(1);
                            })()}
                      </td>
                      <td>{r.campaign.fechaInicio ? new Date(r.campaign.fechaInicio).toLocaleDateString() : '—'}</td>
                      <td>{r.campaign.fechaFin ? new Date(r.campaign.fechaFin).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="rr-pagination">
                <button
                  type="button"
                  className="rr-btn rr-btn--ghost"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  {t('reports.campaigns.prevPage')}
                </button>
                <span className="rr-pagination-label">{t('reports.campaigns.pageOf', { page, total: totalPages })}</span>
                <button
                  type="button"
                  className="rr-btn rr-btn--ghost"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  {t('reports.campaigns.nextPage')}
                </button>
              </div>
            )}
          </>
        )}
      </ReportStatus>
    </div>
  );
}
